// shared-lib/cabinet-runs/incumbents.js
//
// "The fill emits every piece the run owns; where an incumbent exists, the
// emitted piece is merged over it" (proposal §4.2). The fill supplies the
// GEOMETRY — where along the wall, how wide, what it stands under — and the
// incumbent supplies everything else: model, columns, appearance, provenance,
// its id and its place in the room. Two incumbents live here; the appliance
// install piece (`reuseInstallPiece`) and the manual appliance
// (`buildApplianceObjects`) are the same idea inside the fill.
//
// Every function here is pure: it returns REPLACEMENTS (incumbent id → the
// object that takes its place) and never touches the objects it is given.
'use strict';

const { baysForRun } = require('./bays');
const {
  bayResolvesToOvenColumn,
  ovenColumnPartsForBay,
  MIN_SURROUND_PANEL,
} = require('./ovenColumn');
const { buildFarmhouseColumns } = require('../kitchen-assembly/sinkSeat');
const {
  getWallAxes,
  worldToWallParam,
} = require('../kitchen-assembly/wallMath');
const { mayResizeObject } = require('../fixture-types/productRepresentation');
const { MOVE_EPS } = require('./moveAppliances');

const IN = 0.0254;

const isSinkObject = (o) => /sink/.test(String(o?.type || '').toLowerCase());

/** The id of the cabinet `sink` is seated in — its lower-cabinet parent. */
function sinkHostIdOf(sink) {
  if (!isSinkObject(sink)) return null;
  return (
    (sink.parents || []).find((p) => p?.type === 'lower-cabinet')?.id ?? null
  );
}
const short = (id) => String(id).slice(0, 8);

function stamped(o, step, changed, extra = {}) {
  if (!step) return o;
  return {
    ...o,
    scanAdjusted: [
      ...(o.scanAdjusted || []),
      { step, kind: 'modified', changed, ...extra },
    ],
  };
}

/**
 * The cabinet `sink` is seated in. By id — and where a room holds TWO objects
 * under that id (written before `remintTakenId` existed), the one that is
 * this sink's base: the sink's fixture group, or a sink base. Read by id
 * alone, the later of the two won, and that was the plain cabinet the fill
 * had built where the sink used to stand.
 */
function sinkHostOf(sink, objects) {
  const id = sinkHostIdOf(sink);
  if (!id) return null;
  const named = (objects || []).filter((o) => o?.id === id);
  return (
    named.find(
      (o) =>
        (sink.fixtureGroupId && o.fixtureGroupId === sink.fixtureGroupId) ||
        o.category?.sinkBase
    ) ??
    named[0] ??
    null
  );
}

/**
 * Give the emitted box holding `takenId` — any but `except` — a fresh id, and
 * carry every `parents` reference to it in `list` along. In place.
 *
 * The host keeps its id wherever it now stands: the sink and its faucet name
 * it. But a lower cabinet's id is its wall and its START (`buildLowerCabinet`),
 * so the box the fill builds where a moved host USED to start is minted that
 * same id, and two objects under one id draw as one — the new cabinet left a
 * hole where the sink was dragged from (Golden West, Temple City / Scan 1,
 * 2026-10-26). Called before the stand-in's parentage is handed to the host,
 * while `takenId` in a slab's `parents` can still only mean the box.
 */
function remintTakenId(list, takenId, except) {
  const clash = list.findIndex((o) => o !== except && o?.id === takenId);
  if (clash < 0) return;
  const ids = new Set(list.map((o) => o?.id));
  let n = 2;
  while (ids.has(`${takenId}_${n}`)) n += 1;
  const id = `${takenId}_${n}`;
  for (let k = 0; k < list.length; k += 1) {
    const o = list[k];
    if (k === clash) list[k] = { ...o, id };
    else if (
      o !== except &&
      Array.isArray(o?.parents) &&
      o.parents.some((p) => p?.id === takenId)
    ) {
      list[k] = {
        ...o,
        parents: o.parents.map((p) => (p?.id === takenId ? { ...p, id } : p)),
      };
    }
  }
}

/**
 * The sink's host cabinet is RESEATED, not rebuilt.
 *
 * The engine knows where a sink base belongs and how wide it should be; it
 * knows nothing about what `proceduralizeSinks` derived for this one — a
 * farmhouse apron read off the scanned reveal, the open-front cutout
 * metadata, the split door columns, the fixture group tying sink, host and
 * faucet together. Rebuilding the host discards all of it, and copying the
 * attributes across fails on the first one that depends on width (the columns
 * are sized to the OLD cabinet).
 *
 * So take the geometry from the engine and everything else from the scan:
 * move the scanned host onto the reserved span, scaling its column widths to
 * match. The sink ends up centred in a correctly-sized base that is still the
 * object post-processing built.
 *
 * WIDTH is the one thing the span does not always win: a host that SHOWS a
 * catalog product the catalog never called scalable keeps its own width and
 * the disagreement is reported, exactly as `emitTallUnits` treats a tall unit
 * (`mayResizeObject`). Writing the bay's width over a product stretches its
 * mesh. A no-op on the scan corpus — a scanned host carries no model — so this
 * only ever fires on an edit of a kitchen someone has furnished.
 *
 * @returns {{ emitted, replacements }} `emitted` without the stand-in bases,
 *   their slab parentage handed to the hosts; `replacements` host id → host.
 */
function reseatSinkHosts({ emitted, objects, warnings, step }) {
  const out = [...(emitted || [])];
  const replacements = new Map();
  for (const sink of (objects || []).filter(isSinkObject)) {
    const base = out.find((o) => o.applianceId === sink.id);
    const host = sinkHostOf(sink, objects);
    if (!base || !host?.dimensions || host.id === base.id) continue;
    remintTakenId(out, host.id, base);

    const resizable = mayResizeObject(host);
    const width = resizable ? base.dimensions.x : host.dimensions.x;
    if (
      !resizable &&
      Math.abs(base.dimensions.x - host.dimensions.x) > MOVE_EPS
    ) {
      warnings.push(
        `sink host ${short(host.id)}: keeps its product width ${(
          host.dimensions.x / IN
        ).toFixed(1)}" (base ${(base.dimensions.x / IN).toFixed(1)}")`
      );
    }
    const scale = host.dimensions.x > 0 ? width / host.dimensions.x : 1;
    // The host at the base's span — `buildFarmhouseColumns` reads the
    // cabinet's centre and width, so it must see the moved geometry (the
    // server mutated the host before calling it; here the copy is built first).
    const seated = {
      ...host,
      center: { ...host.center, x: base.center.x, z: base.center.z },
      // Its DEPTH from the stand-in too, under the same product rule as the
      // width: the stand-in is built at the run's depth, and centred on the
      // stand-in a host of its own old depth stood short of its neighbours —
      // an island row set to 26" kept a 24" sink base, 1" back from the row on
      // both faces (Golden West, Temple City / Scan 1, 2026-10-22).
      dimensions: {
        ...host.dimensions,
        x: width,
        ...(resizable ? { z: base.dimensions.z } : {}),
      },
      // ORIENTATION comes from the stand-in, not the host: the stand-in is the
      // fill's own box for this span, and the fill is what knows which way the
      // row faces. On a wall run the two always agreed, so this was a no-op and
      // the host's own rotation was carried unexamined. On a FREE-STANDING leg
      // they need not agree — `convertFreeStandingStorageToIslands` synthesizes
      // the two rows at `ry` and `ry + PI`, and which of those the fill calls
      // the front row is decided by `cookSide` — so a reseated host could end
      // up in a row of `ry = -180` cabinets carrying `ry = 0`, with its doors
      // facing into the island (caught by the corpus snapshot on BE8E1430 and
      // 1CFF46CC when islands started routing through this pass, #1808).
      ...(base.rotation ? { rotation: { ...base.rotation } } : {}),
      ...(host.runId ? {} : { runId: base.runId }),
    };
    if (Array.isArray(host.columns)) {
      // A farmhouse apron is sized to the SINK, and the sink does not shrink
      // when the host is reseated onto a narrower span — so scaling it is
      // wrong in a way that shows: E5DED26F's 33" apron in an 87.2" over-merged
      // host became 13.6" in the 36" base, leaving 14" and 8.4" door columns
      // either side, which the renderer drew as 2 + 1 doors around the apron —
      // five fronts under one sink. Rebuild it from the sink instead, through
      // the same builder rule 9.16 uses (its docstring's "runs LAST" stopped
      // being true when this step started reseating hosts after it).
      //
      // Everything else — plain door / drawer columns — genuinely is a
      // fraction of the cabinet, so those still scale.
      const rebuilt = buildFarmhouseColumns(seated, sink);
      seated.columns = rebuilt
        ? rebuilt
        : host.columns.map((c) => ({ ...c, width: c.width * scale }));
    }
    const moved = stamped(seated, step, ['centerXZ', 'width']);
    replacements.set(host.id, moved);
    // Hand the stand-in's PARENTAGE to the host before dropping it. The run
    // countertop lists every cabinet it spans, and `resyncCountertopsToCabinets`
    // later resizes a slab to the union of the parents it can still find — so a
    // slab still naming the removed base collapses onto whatever else it
    // spanned. On 19 Pittsburgh that took a 63.9" counter down to 10.8" and
    // left the sink base bare.
    for (let k = 0; k < out.length; k += 1) {
      const o = out[k];
      if (!Array.isArray(o.parents)) continue;
      const i = o.parents.findIndex((p) => p?.id === base.id);
      if (i < 0) continue;
      out[k] = {
        ...o,
        parents: o.parents.map((p, j) => (j === i ? { ...p, id: host.id } : p)),
      };
    }
    // Drop the engine's stand-in; the scanned host now occupies its span.
    out.splice(out.indexOf(base), 1);
  }
  return { emitted: out, replacements };
}

/**
 * Is `object` the measured tall unit `bay` names — the body `emitTallUnits`
 * re-seats? The ONE test of "this scanned box is a tall unit", shared with
 * the panel (`CabinetBaySection`) so the two cannot disagree: a `tallUnit`
 * bay also anchors a wall oven's column — three objects, not one box — which
 * has its own pass (`emitOvenColumns`) and must never be claimed here.
 *
 * The column is recognised by `bayResolvesToOvenColumn`'s two-armed test —
 * the bay's own spec (a bay produced by this branch's deriver) OR the named
 * object's `ovenHousing` flag (a column whose bay predates the `column` spec
 * and still names only the base cabinet via `applianceId` — a room that has
 * not been reprocessed since). See that function's docstring for why BOTH
 * arms have to stay: dropping the object-flag arm in favour of the bay spec
 * alone would let `emitTallUnits` re-seat a stale bay's base ALONE at the
 * bay's width, tearing it away from its own surround and over-oven cabinet on
 * an ordinary web edit (correction 4 / #1838 follow-up).
 *
 * A true result from `bayResolvesToOvenColumn` is fine on its own here — this
 * is a disjointness guard against `emitOvenColumns`, not a validity check on
 * the bay.
 */
function isReseatedTallUnit(bay, object) {
  return (
    !!bay &&
    bay.kind === 'tallUnit' &&
    !!bay.applianceId &&
    !!object &&
    object.type === 'lower-cabinet' &&
    !bayResolvesToOvenColumn(bay, object)
  );
}

/**
 * Tall units are re-emitted from the incumbent: the scanned unit keeps the
 * height post-processing derived for it and everything else it carries, and
 * takes its geometry from the bay — width from the bay when the object may be
 * resized (`mayResizeObject`: a placeholder may, a product may not). Only the
 * width moves; the height is the scan's finding. A unit already on its bay
 * comes back untouched — no recomputed coordinates, no stamp — so a solve that
 * changed nothing changes nothing.
 *
 * WHERE ALONG THE WALL is the policy's call, and it is the same rule the run
 * applies to appliances (`policy.moveAppliances`, proposal §4.4 / RP2):
 *
 *   - **scan** — `bay.anchorParam`, the deriver's own anchor, which IS the
 *     measured position. The scan site never moves a measured body to a
 *     solve-time clamp: it packs around the object where it stands and reports
 *     the clamp in `out.unsatisfied` (see the "NOT moved here" note in
 *     `materializeScanRuns.js`). A bay the packer clamped therefore leaves the
 *     unit exactly where the scan found it. The deriver moving the anchor
 *     itself — `absorbIntoTallUnit` widening the bay into a run-end reveal —
 *     is a decision about the SCAN's own geometry and does move the unit.
 *   - **edit** — `sb.center`, the solved centre. The first edit is the opt-in
 *     that hands the run its appliances' positions (layout-relationships
 *     §2.5), and a tall unit is one of them.
 *
 * An oven column is NOT handled here (proposal §9 step 7): its bay anchors
 * three sections, not one box, so it is `emitOvenColumns`'s
 * (`isReseatedTallUnit` is the disjointness guard between the two).
 *
 * @param {Object} policy `policyFor(site, run)` — required; there is no
 *   defensible default for "does this site move a measured body".
 */
function emitTallUnits({ resolve, objects, warnings, step, policy }) {
  if (!policy) throw new Error('emitTallUnits: a policy is required');
  const replacements = new Map();
  const run = resolve?.run;
  if (!run) return { replacements };
  const bayById = new Map(baysForRun(run).map((b) => [b.id, b]));
  const byId = new Map(
    (objects || []).filter((o) => o?.id).map((o) => [o.id, o])
  );
  for (const sb of resolve.bays || []) {
    const bay = bayById.get(sb.bayId);
    if (!bay?.applianceId) continue;
    const inc = byId.get(bay.applianceId);
    if (!isReseatedTallUnit(bay, inc)) continue;
    const wall = run.segments?.[sb.segIndex]?.wall;
    const axes = wall ? getWallAxes(wall) : null;
    // Where the unit actually stands, read before anything decides to move it:
    // it is both the "did it move" reference below and the HOLD position an
    // unsatisfied bay seats at (`seatParamForIncumbent`).
    const currentParam =
      axes && inc.center ? worldToWallParam(wall, inc.center) : null;
    // The site decides which param seats the unit; a bay carrying neither is
    // one this pass cannot honour, and saying so beats leaving it where it
    // stands and calling that the answer. An unsatisfied bay overrides the
    // site and holds the unit where it is — see `seatParamForIncumbent`.
    const along = seatParamForIncumbent({
      resolve,
      bay,
      sb,
      policy,
      warnings,
      label: `tall unit ${short(inc.id)}`,
      current: currentParam,
    });
    if (
      !axes ||
      !(inc.dimensions?.x > 0) ||
      !inc.center ||
      !Number.isFinite(along)
    ) {
      warnings.push(`tall unit ${short(inc.id)}: could not seat onto its bay`);
      continue;
    }
    const current = currentParam;
    const resizable = mayResizeObject(inc);
    const width = resizable ? bay.width : inc.dimensions.x;
    if (!resizable && Math.abs(bay.width - inc.dimensions.x) > MOVE_EPS) {
      warnings.push(
        `tall unit ${short(inc.id)}: keeps its product width ${(
          inc.dimensions.x / IN
        ).toFixed(1)}" (bay ${(bay.width / IN).toFixed(1)}")`
      );
    }
    const moves = current == null || Math.abs(along - current) > MOVE_EPS;
    const widens = Math.abs(width - inc.dimensions.x) > MOVE_EPS;
    const base = {
      ...inc,
      ...(inc.runId ? {} : { runId: run.id }),
      ...(inc.bayId ? {} : { bayId: bay.id }),
    };
    if (!moves && !widens) {
      replacements.set(inc.id, base);
      continue;
    }
    // Only the along-wall axis moves; the depth offset the scan gave it stays.
    const perp =
      (inc.center.x - axes.start.x) * axes.wallNormal.x +
      (inc.center.z - axes.start.z) * axes.wallNormal.z;
    const scale = width / inc.dimensions.x;
    const changed = [
      ...(moves ? ['centerXZ'] : []),
      ...(widens ? ['width'] : []),
    ];
    const next = {
      ...base,
      center: {
        ...inc.center,
        x: axes.start.x + along * axes.wallDir.x + perp * axes.wallNormal.x,
        z: axes.start.z + along * axes.wallDir.z + perp * axes.wallNormal.z,
      },
      dimensions: { ...inc.dimensions, x: width },
      ...(Array.isArray(inc.columns) && widens
        ? {
            columns: inc.columns.map((c) => ({ ...c, width: c.width * scale })),
          }
        : {}),
    };
    replacements.set(
      inc.id,
      stamped(
        next,
        step,
        changed,
        bay.absorbedReveal > 0
          ? { absorbedRevealIn: bay.absorbedReveal / IN }
          : {}
      )
    );
  }
  return { replacements };
}

/**
 * WHERE ALONG THE WALL a column seats. Normally the policy's call — `sb.center`
 * on the edit site, `bay.anchorParam` (the measured position) on the scan site,
 * exactly as for a tall unit — with ONE override, whatever the site:
 *
 * **Do not move a measured body to a position the run itself reports it could
 * not place.** A bay named in `resolve.unsatisfied` is one the packer clamped
 * into the span or found overlapping its neighbour (`spanPacking.js`); its
 * solved centre is where the piece FITTED, not where it belongs. A column is
 * three measured sections plus the scanned oven standing in them, so honouring
 * such a centre slides four measured bodies at once — on 26767201 that is 27"
 * from a 70.5" anchor to a 97.5" solve, landing inside the pantry bay next
 * door, for no geometric gain: the column's real span abuts that pantry
 * exactly and never needed clamping. A reservation the run cannot satisfy is
 * not a better answer than the scan's own.
 *
 * The report is the gate, so there is no distance threshold to invent — and
 * the warning fires only when the two params actually disagree (`MOVE_EPS`,
 * the same "did it move" epsilon `seatColumnPart` uses), so an unsatisfied bay
 * the packer left standing where it was says nothing. That is the `overlap`
 * case by construction: `planSubSpanSequence` leaves both overlapping pieces
 * where they stand, so the solve already equals the anchor.
 *
 * `label` names the thing in the warning, because each pass already names its
 * subject its own way — the column by its BAY, a tall unit by its OBJECT.
 *
 * **`current` — where the body actually stands — is the hold position, not
 * `bay.anchorParam`.** The two agree on every sound run (measured across dev:
 * identical to 0.1" on 4 of the 5 structures with an unsatisfied tall bay).
 * They part company when the stored anchor is STALE: Montrose / Scan 1 carries
 * two tall bays whose `anchorParam` is NEGATIVE — off the start of the wall,
 * both by the same -155.1" — because the run's segment was rebased and the bay
 * anchors never travelled with it. Seating at that anchor MOVES the pantry
 * 155", which is the opposite of holding it. The old code hid this by always
 * using `sb.center`, which is frame-current by construction.
 *
 * So "do not move a measured body" is taken literally: leave it where it is.
 * `bay.anchorParam` remains the fallback when the caller cannot say where the
 * body is (no wall, no centre) — there is nothing better then.
 */
function seatParamForIncumbent({
  resolve,
  bay,
  sb,
  policy,
  warnings,
  label,
  current,
}) {
  const solved = policy.moveAppliances ? sb.center : bay.anchorParam;
  const blocked = (resolve?.unsatisfied || []).find((u) => u?.bayId === bay.id);
  if (!blocked) return solved;
  const anchor = Number.isFinite(current) ? current : bay.anchorParam;
  if (
    Number.isFinite(solved) &&
    Number.isFinite(anchor) &&
    Math.abs(solved - anchor) > MOVE_EPS
  ) {
    warnings.push(
      `${label}: kept at its measured position — the run ` +
        `reports this bay ${blocked.reason} and solved it ` +
        `${(Math.abs(solved - anchor) / IN).toFixed(1)}" away`
    );
  }
  return anchor;
}

/**
 * ONE width for the whole column, and the section that decided it: a section
 * showing a catalog product pins the column, exactly as a product pantry pins
 * its bay (`emitTallUnits`).
 *
 * A section with no readable width can never be the pin. `mayResizeObject`
 * reads the MODEL fields alone, so a section carrying a `modelId`/`modelUrl`
 * and no `dimensions` at all passes its test — and reading `dimensions.x` off
 * it to report the disagreement THREW, out of `mergeResolvedRuns`, which
 * nothing wraps: one malformed section aborted a whole reprocess or edit
 * commit. This file warns and continues (see the header). The extra test is
 * the same one `seatColumnPart` refuses on, so such a section is already
 * reported by name in the seat loop and needs nothing more here.
 *
 * @returns {{ width: number|undefined, pinned: Object|null }}
 */
function columnWidth(bay, parts, warnings) {
  const pinned = parts.find((p) => !mayResizeObject(p) && p.dimensions?.x > 0);
  if (!pinned) return { width: bay.width, pinned: null };
  if (Math.abs(bay.width - pinned.dimensions.x) > MOVE_EPS) {
    warnings.push(
      `oven column ${short(bay.id)}: keeps its product width ${(
        pinned.dimensions.x / IN
      ).toFixed(1)}" (bay ${(bay.width / IN).toFixed(1)}")`
    );
  }
  return { width: pinned.dimensions.x, pinned };
}

/**
 * The width THIS section takes: the column's, unless the section is itself a
 * product the catalog never called scalable AND is not the section that pinned
 * the column.
 *
 * `mayResizeObject` is a per-OBJECT veto — `emitTallUnits` and
 * `reseatSinkHosts` both honour it one object at a time — but a column's pin
 * is only ever ONE section, and a column may hold more than one product. A 10"
 * product base pinning the column wrote a 36" product over-oven cabinet down
 * to 10", stretching its mesh, with the only warning naming the bay-vs-pin
 * disagreement so it read as working correctly.
 *
 * `null` means "leave this section's own dimensions alone" — the same value
 * the oven is already seated with. `warnings` is required rather than
 * optional: a silently dropped warning here is the exact failure this
 * function exists to fix.
 */
function widthForPart(inc, { width, isPin, bay, warnings }) {
  if (width == null || isPin || mayResizeObject(inc)) return width;
  if (Math.abs(width - inc.dimensions.x) > MOVE_EPS) {
    warnings.push(
      `oven column ${short(bay.id)}: section ${short(inc.id)} keeps its ` +
        `product width ${(inc.dimensions.x / IN).toFixed(1)}" (column ${(
          width / IN
        ).toFixed(1)}")`
    );
  }
  return null;
}

/**
 * An oven column is re-emitted from its bay as ONE piece.
 *
 * `buildOvenHousing` builds a column as a STACK — a base cabinet under the
 * appliance, a surround around it, an over-oven cabinet above — because one
 * floor-to-ceiling box would bury its own door handles behind the oven. Three
 * objects, one bay. Until proposal §9 step 7 the run held that bay as a pure
 * reservation and exempted the base from its own sweep (`keepRule`), which
 * meant the column did not move when its bay did, did not resize when its bay
 * did, and lost its over-oven cabinet the moment the uppers were rebuilt.
 *
 * This is `emitTallUnits` over a set: the fill supplies the along-wall
 * geometry and the width, and each section supplies everything else. **No
 * vertical band is ever recomputed** — the scan cut those bands at the oven's
 * real underside and top, and no number the run holds is a better answer.
 *
 * WHERE ALONG THE WALL is the policy's call (`columnSeatParam`) — with the
 * override that a bay the run reports UNSATISFIED seats at its measured
 * anchor whatever the site. WHAT WIDTH is one decision for the column
 * (`columnWidth`) that any section's own product may still veto for itself
 * (`widthForPart`).
 *
 * @param {Object} policy `policyFor(site, run)` — required.
 */
function emitOvenColumns({ resolve, objects, warnings, step, policy }) {
  if (!policy) throw new Error('emitOvenColumns: a policy is required');
  const replacements = new Map();
  const run = resolve?.run;
  if (!run) return { replacements };
  const bayById = new Map(baysForRun(run).map((b) => [b.id, b]));
  for (const sb of resolve.bays || []) {
    const bay = bayById.get(sb.bayId);
    const healed = ovenColumnPartsForBay(bay, objects);
    if (healed === null) continue;
    const { parts, ovenId } = healed;
    if (!parts.length) {
      warnings.push(`oven column ${short(bay.id)}: no sections left to emit`);
      continue;
    }
    const wall = run.segments?.[sb.segIndex]?.wall;
    const axes = wall ? getWallAxes(wall) : null;
    // The column's own position, from the part the bay names (the lowest
    // section) — the hold position when the run cannot place this bay.
    const root = parts.find((pt) => pt?.id === bay.applianceId) || parts[0];
    const currentParam =
      axes && root?.center ? worldToWallParam(wall, root.center) : null;
    const along = seatParamForIncumbent({
      resolve,
      bay,
      sb,
      policy,
      warnings,
      label: `oven column ${short(bay.id)}`,
      current: currentParam,
    });
    if (!axes || !Number.isFinite(along)) {
      warnings.push(
        `oven column ${short(bay.id)}: could not seat onto its bay`
      );
      continue;
    }
    const { width, pinned } = columnWidth(bay, parts, warnings);
    for (const inc of parts) {
      const next = seatColumnPart(inc, {
        axes,
        along,
        width,
        isPin: inc === pinned,
        wall,
        run,
        bay,
        step,
        warnings,
      });
      if (next) {
        replacements.set(inc.id, next);
      } else {
        // A malformed section (no `center`, no positive `dimensions.x`) is
        // dropped, not silently skipped — `emitTallUnits` warns the same way
        // for the same shape, and `parts.length > 0` here means the
        // top-level "no sections left to emit" warning above never fires for
        // this case even when EVERY section turns out unseatable.
        warnings.push(
          `oven column ${short(bay.id)}: section ${short(inc.id)} could not be seated onto its bay`
        );
      }
    }
    // The appliance itself: the run POSITIONS it (a `bayId` stamp, never a
    // `runId` — the run does not emit an oven, it just moves it), so it
    // travels with the column it stands in. It keeps its own size: an oven is
    // a product in a hole, and widening the column widens the surround's
    // side panels, not the appliance (`width: null` below). Silently absent
    // when the bay names no oven at all (a plain tall unit already exempted
    // upstream, or a healed column whose oven `ovenInColumn` could not find)
    // — either way the three sections above still emit fine without it, and
    // there is nothing to warn about because there is no oven to fail to
    // seat.
    const ovenInc = ovenId
      ? (objects || []).find((o) => o?.id === ovenId)
      : null;
    if (ovenInc) {
      const next = seatColumnPart(ovenInc, {
        axes,
        along,
        width: null,
        wall,
        run,
        bay,
        step,
        warnings,
        stampRunId: false,
      });
      if (next) {
        replacements.set(ovenInc.id, next);
      } else {
        // Consistent with the sections' own malformed-object warning above:
        // a bay DOES name this oven, but it cannot be seated (no `center`, no
        // positive `dimensions.x`) — dropped, not silently skipped.
        warnings.push(
          `oven column ${short(bay.id)}: oven ${short(ovenInc.id)} could not be seated onto its bay`
        );
      }
    }
  }
  return { replacements };
}

/**
 * One section, seated. Only the along-wall axis and the width move; the
 * section's own depth offset, its vertical band and everything it carries
 * stay. Returns the incumbent with stamps only when nothing else changed, so
 * a solve that changed nothing changes nothing.
 *
 * `isPin` says this section is the one that decided the column's width, so
 * `widthForPart` may write it even though the section is a product; every
 * other product in the column keeps its own and is named in `warnings`.
 *
 * `stampRunId` (default `true`) is the switch between the two ownership rows
 * this one function seats: the three column SECTIONS the run builds and
 * emits every solve (`runId` + `bayId`), and the oven standing inside them,
 * which the run only POSITIONS (`bayId` alone, never `runId` — proposal §6
 * row 2). `width: null` already leaves a caller's dimensions untouched; this
 * is the other half of "an oven is a product in a hole," not a rebuild of the
 * same idea.
 */
function seatColumnPart(
  inc,
  {
    axes,
    along,
    width,
    isPin = false,
    wall,
    run,
    bay,
    step,
    warnings,
    stampRunId = true,
  }
) {
  if (!inc?.center || !(inc.dimensions?.x > 0)) return null;
  // The column's width, unless this section's own product vetoes it.
  const seatWidth = widthForPart(inc, { width, isPin, bay, warnings });
  const base = {
    ...inc,
    ...(inc.runId || !stampRunId ? {} : { runId: run.id }),
    ...(inc.bayId ? {} : { bayId: bay.id }),
  };
  const current = worldToWallParam(wall, inc.center);
  const moves = current == null || Math.abs(along - current) > MOVE_EPS;
  const widens =
    seatWidth != null && Math.abs(seatWidth - inc.dimensions.x) > MOVE_EPS;
  if (!moves && !widens) return base;
  const perp =
    (inc.center.x - axes.start.x) * axes.wallNormal.x +
    (inc.center.z - axes.start.z) * axes.wallNormal.z;
  const scale = widens ? seatWidth / inc.dimensions.x : 1;
  const dimensions = { ...inc.dimensions, ...(widens ? { x: seatWidth } : {}) };
  // The surround renders as two panels FLANKING the oven, so its panel
  // thickness is the leftover width either side of the appliance — widen the
  // column without widening the panels and the oven floats in a gap
  // (`buildOvenHousing`: `sidePanelThickness = (columnWidth - ovenWidth) / 2`).
  // `widens` fires on a NARROWING too (it is an inequality, not a direction),
  // and a non-scalable product pinning the column to a width narrower than
  // its current one is a real path (`emitOvenColumns`'s `pinned` branch) —
  // so this must floor at the same `MIN_SURROUND_PANEL` the producer clamps
  // to, or the panel goes to zero/negative and the renderer's `t <= 0` guard
  // silently substitutes its own nominal 1/2", stranding the oven in a gap.
  if (widens && Number.isFinite(inc.dimensions.sidePanelThickness)) {
    dimensions.sidePanelThickness = Math.max(
      MIN_SURROUND_PANEL,
      inc.dimensions.sidePanelThickness + (seatWidth - inc.dimensions.x) / 2
    );
  }
  const next = {
    ...base,
    center: {
      ...inc.center,
      x: axes.start.x + along * axes.wallDir.x + perp * axes.wallNormal.x,
      z: axes.start.z + along * axes.wallDir.z + perp * axes.wallNormal.z,
    },
    dimensions,
    ...(Array.isArray(inc.columns) && widens
      ? { columns: inc.columns.map((c) => ({ ...c, width: c.width * scale })) }
      : {}),
  };
  return stamped(next, step, [
    ...(moves ? ['centerXZ'] : []),
    ...(widens ? ['width'] : []),
  ]);
}

module.exports = {
  reseatSinkHosts,
  emitTallUnits,
  emitOvenColumns,
  isReseatedTallUnit,
  sinkHostIdOf,
};
